import React from "react";
import {
  DropOverride,
  ELightPath,
  PlateTypeCaptureProfile,
  Roi,
} from "../../../Model/CaptureProfile";
import EOpticAndCameraFeature from "../../../Model/EOpticAndCameraFeature";
import {
  CropZoom,
  LightPathCapability,
  ObjectiveZoom,
} from "../../../Model/ImagerCapabilities";
import { PlateType } from "../../../Model/PlateType";
import ImagerUtil from "../../../Utility/ImagerUtil";
import DefaultImagingSettingCheckBox from "./DefaultImagingSettingCheckBox";
import DefaultImagingSettingComboBox from "./DefaultImagingSettingComboBox";
import DefaultImagingSettingTextBox from "./DefaultImagingSettingTextBox";
import RoiOptions from "./RoiOptions";

const DROP_OPTION_OVERRIDE_EXIST = "dropOptionOverrideExist";

type Props = {
  style?: React.CSSProperties;
  captureProfile: PlateTypeCaptureProfile;
  plateType: PlateType;
  setCaptureProfile: (captureProfile: PlateTypeCaptureProfile) => void;
  onErrorOccur: (key: string) => void;
  onErrorCleared: (key: string) => void;
  objectiveZoom: ObjectiveZoom;
  cropZoom?: CropZoom;
  dropOptionOverrides: DropOverride[];
  disabled: boolean;
  lightPath: ELightPath;
  lightPathCapability: LightPathCapability;
};

export default function ObjectiveZoomDropOverride({
  style = {},
  captureProfile,
  setCaptureProfile,
  onErrorCleared,
  onErrorOccur,
  plateType,
  objectiveZoom,
  cropZoom,
  dropOptionOverrides,
  disabled,
  lightPath,
  lightPathCapability,
}: Props) {
  const defaultRoi: Roi = {
    autoSelectObjective: true,
    fixedObjective: objectiveZoom?.objectives[0].value ?? 1,
  };

  const dropLocationCaptureProperty = captureProfile.dropLocation;

  if (captureProfile.dropLocation == null) {
    throw new Error("Drop location capture property is null");
  }

  if (dropLocationCaptureProperty == null) {
    throw new Error("Drop location capture property is null");
  }

  const numberOfDropPerWell = plateType.numberOfDropPerWell;
  const useDropOptionOverrides =
    captureProfile.dropLocation.dropOptionOverrideExist;

  const onObjectiveZoomOverrideChanged = (
    index: number,
    objectiveZoom: number
  ) => {
    let dropOverrides = [...dropOptionOverrides];
    dropOverrides[index] = {
      ...dropOptionOverrides[index],
      objectiveZoom: objectiveZoom,
    };
    setCaptureProfile({
      ...captureProfile,
      dropOverrides: dropOverrides,
    });
  };

  const onCropZoomOverrideChanged = (index: number, cropZoom: number) => {
    let dropOverrides = [...dropOptionOverrides];
    dropOverrides[index] = {
      ...dropOptionOverrides[index],
      cropZoom: cropZoom,
    };
    setCaptureProfile({
      ...captureProfile,
      dropOverrides: dropOverrides,
    });
  };

  const onRoiOverrideChanged = (index: number, roi: Roi) => {
    let dropOverrides = [...dropOptionOverrides];
    dropOverrides[index] = {
      ...dropOptionOverrides[index],
      roi: roi,
    };
    setCaptureProfile({
      ...captureProfile,
      dropOverrides: dropOverrides,
    });
  };

  const onUseDropOverrideChanged = (dropOptionOverrideExist: boolean) => {
    let dropOverrides = [...dropOptionOverrides];
    setCaptureProfile({
      ...captureProfile,
      dropLocation: {
        ...dropLocationCaptureProperty,
        dropOptionOverrideExist: dropOptionOverrideExist,
      },
      dropOverrides: dropOverrides,
    });
  };

  const objectiveZoomDisplayValues =
    ImagerUtil.getObjectiveZoomDisplayValues(objectiveZoom);

  let dropZoomOverrideInputs = [];
  if (dropLocationCaptureProperty.dropOptionOverrideExist) {
    for (let i = 0; i < numberOfDropPerWell; i++) {
      dropZoomOverrideInputs.push(
        <div key={`drop-override-${i}`}>
          <div
            style={{
              textAlign: "left",
              marginBottom: 7.5,
              marginTop: 7.5,
              fontWeight: 700,
            }}
          >{`Drop ${i + 1}`}</div>
          <DefaultImagingSettingComboBox
            displayValues={objectiveZoomDisplayValues}
            value={
              dropOptionOverrides != null && dropOptionOverrides.length > i
                ? dropOptionOverrides[i]?.objectiveZoom?.toFixed(1) ??
                  objectiveZoomDisplayValues[0].value
                : objectiveZoomDisplayValues[0].value
            }
            onChange={(name, value) =>
              onObjectiveZoomOverrideChanged(i, Number.parseFloat(value))
            }
            label={`Objective`}
            name={`Drop Override ${i} ${EOpticAndCameraFeature.ObjectiveZoom}`}
            unit=""
            style={{
              paddingLeft: "13px",
            }}
            disabled={!useDropOptionOverrides}
          />
          {cropZoom && (
            <DefaultImagingSettingTextBox
              value={
                dropOptionOverrides != null && dropOptionOverrides.length > i
                  ? dropOptionOverrides[i]?.cropZoom ?? cropZoom.minCropZoom
                  : cropZoom.minCropZoom
              }
              onChange={(name, value) => onCropZoomOverrideChanged(i, value)}
              label="Zoom"
              name={`Drop Override ${i} Crop Zoom`}
              unit="x"
              min={cropZoom.minCropZoom}
              max={cropZoom.maxCropZoom}
              disabled={!useDropOptionOverrides}
              style={{
                paddingLeft: "13px",
                marginTop: 7.5,
                marginBottom: 7.5,
              }}
              isInteger={false}
              onErrorOccur={(name) => onErrorOccur(name)}
              onErrorCleared={(name) => onErrorCleared(name)}
            />
          )}
          <RoiOptions
            roi={
              dropOptionOverrides != null && dropOptionOverrides.length > i
                ? dropOptionOverrides[i]?.roi ?? defaultRoi
                : defaultRoi
            }
            onValueChanged={(roi) => onRoiOverrideChanged(i, roi)}
            lightPath={lightPath}
            lightpathCapability={lightPathCapability}
            style={{ paddingLeft: 13 }}
            radioButtonId={i}
            titleStyle={{ margin: "10px 0px" }}
            disabled={!useDropOptionOverrides}
          />
        </div>
      );
    }
  }

  return (
    <div>
      <DefaultImagingSettingCheckBox
        checked={
          !disabled && dropLocationCaptureProperty.dropOptionOverrideExist
        }
        onChange={(name, value) => onUseDropOverrideChanged(value)}
        label="Override Zoom"
        name={DROP_OPTION_OVERRIDE_EXIST}
        disabled={disabled}
        style={{ margin: "24px 0px", fontWeight: "bold" }}
      />
      {!disabled &&
        dropLocationCaptureProperty.dropOptionOverrideExist &&
        dropZoomOverrideInputs}
    </div>
  );
}
